import React, { useState, useEffect } from 'react';
import { CheckCircle, ArrowRight, Clock3, List, Layers } from 'lucide-react';
import HighchartsLineChart from '../../components/HighchartsLineChart';
import toast from 'react-hot-toast';
import Sidebar from '../../components/admin/Sidebar';
import Header from '../../components/admin/Header';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { hasPendingReviewForRole } from '../../utils/helpers';
import './Dashboard.css';

const AGING_THRESHOLD_DAYS = 7;
const AGING_QUEUE_LABELS = ['Changes requested', 'Needs final copy', 'Stage 1', 'Stage 2', 'Stage 3', 'Stage 4'];

const supplierId = supplier => String(supplier?._id || supplier || '');
const getSubmission = (artwork, stageNum, id) =>
  (artwork[`stage${stageNum}`]?.submissions || []).find(submission => supplierId(submission?.supplierId) === id);
const getAssignedSupplierIds = artwork => (artwork.supplierAssignmentInfo?.assignedSuppliers?.length
  ? artwork.supplierAssignmentInfo.assignedSuppliers
  : artwork.suppliers || []).map(supplierId).filter(Boolean);
const isStage4ReviewComplete = submission => ['rd', 'regulatory', 'qa', 'cco'].every(role => {
  const approvals = (submission?.approvals || []).filter(approval => approval.role === role);
  if (!approvals.length) return false;
  const hasHead = ['rd', 'regulatory', 'qa'].includes(role) && approvals.some(approval => approval.teamLevel === 'head');
  return hasHead ? approvals.some(approval => approval.teamLevel === 'head' && approval.status === 'approved') : approvals.every(approval => approval.status === 'approved');
});
const supplierMatchesQueue = (artwork, id, queue) => {
  const stage3 = getSubmission(artwork, 3, id);
  const stage4 = getSubmission(artwork, 4, id);
  const hasChanges = [stage3, stage4].some(submission => (submission?.approvals || []).some(approval => approval.status === 'changes_requested'));
  if (queue === 'stage3') return stage3?.status !== 'completed' && !hasChanges;
  if (queue === 'stage4') return stage3?.status === 'completed' && Boolean(stage4) && stage4.status !== 'completed' && !isStage4ReviewComplete(stage4) && !hasChanges;
  if (queue === 'finalize') return isStage4ReviewComplete(stage4) && !stage4?.adminApproval?.uploadedAt;
  return false;
};
const artworkHasSupplierInQueue = (artwork, queue) => getAssignedSupplierIds(artwork).some(id => supplierMatchesQueue(artwork, id, queue));
const isArtworkFullyCompleted = artwork => {
  const ids = getAssignedSupplierIds(artwork);
  return ids.length > 0 && ids.every(id => {
    const stage3 = getSubmission(artwork, 3, id);
    const stage4 = getSubmission(artwork, 4, id);
    return stage3?.status === 'completed' && stage4?.status === 'completed' && Boolean(stage4.adminApproval?.uploadedAt);
  });
};

const hasArtworkChangesRequested = artwork => [1, 2, 3, 4].some(stageNum => {
  const stage = artwork[`stage${stageNum}`] || {};
  return stage.status === 'changes_requested' ||
    (stage.approvals || []).some(approval => approval.status === 'changes_requested') ||
    (stage.submissions || []).some(submission =>
      (submission.approvals || []).some(approval => approval.status === 'changes_requested')
    );
});

const getArtworkQueueLabel = artwork => {
  if (hasArtworkChangesRequested(artwork)) return 'Changes requested';
  if (artworkHasSupplierInQueue(artwork, 'stage3')) return 'Stage 3';
  if (artworkHasSupplierInQueue(artwork, 'stage4')) return 'Stage 4';
  if (artworkHasSupplierInQueue(artwork, 'finalize')) return 'Needs final copy';
  return `Stage ${artwork.currentStage}`;
};

const getDaysSinceUpdate = updatedAt => {
  const timestamp = Date.parse(updatedAt);
  if (Number.isNaN(timestamp)) return null;
  return Math.max(0, Math.floor((Date.now() - timestamp) / 86400000));
};

export default function AdminDashboard() {
  const { user } = useAuth();
  const [artworks, setArtworks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const query = new URLSearchParams();
        query.set('summary', 'true');
        const res = await api.get(`/artworks?${query.toString()}`);
        setArtworks(res.data);
      } catch {} finally {}
    })();
  }, []);

  useEffect(() => {
    const handleRefresh = async () => {
      try {
        const query = new URLSearchParams();
        query.set('summary', 'true');
        const res = await api.get(`/artworks?${query.toString()}`);
        setArtworks(res.data);
        toast.success('Data refreshed');
      } catch {
        toast.error('Failed to refresh data');
      }
    };

    window.addEventListener('navigationRefresh', handleRefresh);
    return () => window.removeEventListener('navigationRefresh', handleRefresh);
  }, []);

  // Artworks needing my attention: compute per-stage by looking at each stage's approvals
  const myRole = user?.role;
  const [selectedStage, setSelectedStage] = useState(1);
  const pendingByStage = { 1: [], 2: [], 3: [], 4: [] };

  artworks.forEach(a => {
    if (a.overallStatus !== 'active' && isArtworkFullyCompleted(a)) return;
    [1, 2, 3, 4].forEach(stageNum => {
      const stage = a[`stage${stageNum}`];
      if (!stage) return;
      const pendingInStage = hasPendingReviewForRole(stage, myRole, a, stageNum, user?._id || user?.id, user?.email, user?.name);
      if (pendingInStage) pendingByStage[stageNum].push(a);
    });
  });

  // Flatten my pending artworks across stages for the "All" view
  const pendingMine = [...new Set([].concat(...Object.values(pendingByStage)))];
  const displayedPending = selectedStage === 'all' ? pendingMine : pendingByStage[selectedStage];
  const pendingKpis = [
    { key: 'all', label: 'All pending reviews', count: pendingMine.length, icon: List, color: '#be123c', background: '#fff1f2' },
    ...[1, 2, 3, 4].map(stage => ({
      key: stage,
      label: `Stage ${stage}`,
      count: pendingByStage[stage].length,
      icon: Layers,
      color: ['#be123c', '#b45309', '#047857', '#0369a1'][stage - 1],
      background: ['#fff1f2', '#fffbeb', '#ecfdf5', '#f0f9ff'][stage - 1]
    }))
  ];
  const agingArtworks = artworks
    .filter(artwork => artwork.overallStatus === 'active' || !isArtworkFullyCompleted(artwork))
    .map(artwork => ({
      ...artwork,
      ageDays: getDaysSinceUpdate(artwork.updatedAt),
      queueLabel: getArtworkQueueLabel(artwork)
    }))
    .filter(artwork => artwork.ageDays !== null && artwork.ageDays >= AGING_THRESHOLD_DAYS)
    .sort((first, second) => second.ageDays - first.ageDays);
  const agingQueueCounts = AGING_QUEUE_LABELS.map(label => ({
    label,
    count: agingArtworks.filter(artwork => artwork.queueLabel === label).length
  }));

  return (
    <div className="page-layout" style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <Sidebar />
      <div className="main-content">
        <Header
          title={`Welcome back, ${user?.name?.split(' ')[0]}`}
          subtitle="Here's your artwork approval overview"
        />
        <div className="page-body">
          <div className="page-container">

            <div className="dashboard-grid">
              {/* Admin progression chart */}
              {user?.role === 'admin' && (
                <div className="card dashboard-section" style={{flex:1}}>
                  <div className="section-header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <h3 style={{margin:0}}>Progression by Stage</h3>
                    <span className="badge badge-info">Overview</span>
                  </div>
                  <div style={{padding:16}}>
                    {(() => {
                       // Stage 3 onward is supplier-specific: an artwork stays in every
                       // queue where one of its assigned suppliers still needs that step.
                       const stageCounts = [1,2,3,4].map(s => artworks.filter(a =>
                         s >= 3
                           ? artworkHasSupplierInQueue(a, `stage${s}`)
                           : a.overallStatus === 'active' && a.currentStage === s
                       ).length);
                       const needsFinalCopyCount = artworks.filter(a => artworkHasSupplierInQueue(a, 'finalize')).length;
                       const completedCount = artworks.filter(isArtworkFullyCompleted).length;
                      const changesRequestedCount = artworks.filter(a =>
                        ['stage1', 'stage2', 'stage3', 'stage4'].some(stage => a[stage]?.status === 'changes_requested')
                      ).length;
                      const chartCounts = [...stageCounts, needsFinalCopyCount, completedCount, changesRequestedCount];
                      const chartLabels = ["Stage 1","Stage 2","Stage 3","Stage 4","Needs final copy","Completed","Changes requested"];
                      return (
                        <div style={{width:'100%'}}>
                          <HighchartsLineChart labels={chartLabels} data={chartCounts} height={260} />
                          <div style={{display:'flex', gap:12, marginTop:8, justifyContent:'flex-end', flexWrap:'wrap'}}>
                            {chartCounts.map((c, i) => (
                              <div key={i} style={{fontSize:12, color:'#374151'}}><strong>{chartLabels[i]}:</strong> {c}</div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}
              {/* Pending My Action */}
              {myRole !== 'admin' && (
                <div className="card dashboard-section" style={{flex: 1}}>
                    <div className="section-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between', gap:12}}>
                      <div style={{display:'flex', alignItems:'center', gap:12}}>
                        <h3 style={{margin:0}}>Pending Your Review</h3>
                        <span className="badge badge-danger">{pendingMine.length}</span>
                      </div>
                    </div>
                    <div className="pending-review-kpis" aria-label="Pending review filters">
                      {pendingKpis.map(({ key, label, count, icon: Icon, color, background }) => (
                        <button
                          key={key}
                          type="button"
                          className={`pending-review-kpi ${selectedStage === key ? 'active' : ''}`}
                          onClick={() => setSelectedStage(key)}
                          aria-pressed={selectedStage === key}
                        >
                          <span className="pending-review-kpi-icon" style={{color, background}}><Icon size={17} strokeWidth={2.2} /></span>
                          <span>
                            <span className="pending-review-kpi-label">{label}</span>
                            <span className="pending-review-kpi-value">{count}</span>
                          </span>
                        </button>
                      ))}
                    </div>
                  {displayedPending.length === 0 ? (
                    <div className="empty-state" style={{padding:'32px'}}>
                      <CheckCircle size={36} />
                      <h3>All caught up!</h3>
                      <p>No artworks pending your review right now.</p>
                    </div>
                  ) : (
                    <div className="pending-review-list">
                      {displayedPending.map((a, index) => (
                        <button
                          key={a._id}
                          type="button"
                          className="pending-review-card"
                          onClick={() => window.location.href = `/artworks/${a._id}`}
                        >
                          <div className="pending-review-left">
                            <div className="pending-review-index">{index + 1}</div>
                            <div className="pending-review-body">
                              <p className="pending-review-title">{a.title}</p>
                              <p className="pending-review-meta">Stage {a.currentStage} · Updated {new Date(a.updatedAt).toLocaleDateString('en-NG')}</p>
                            </div>
                          </div>
                          <div className="pending-review-right">
                            <span className={`badge badge-stage${a.currentStage}`}>Stage {a.currentStage}</span>
                            <ArrowRight size={18} />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

            </div>
            {user?.role === 'admin' && (
              <section className="card dashboard-section aging-section" aria-labelledby="aging-heading">
                <div className="section-header">
                  <h3 id="aging-heading" style={{margin:0}}>Aging &amp; bottlenecks</h3>
                  <span className="badge badge-warning"><Clock3 size={13} /> 7+ days since update</span>
                </div>
                <div className="aging-queue-grid">
                  {agingQueueCounts.map(({ label, count }) => (
                    <div className="aging-queue-item" key={label}>
                      <span>{label}</span>
                      <strong>{count}</strong>
                    </div>
                  ))}
                </div>
                {agingArtworks.length === 0 ? (
                  <div className="aging-empty">No active artworks have gone 7 days without an update.</div>
                ) : (
                  <div className="aging-work-list">
                    {agingArtworks.slice(0, 5).map(artwork => (
                      <Link
                        key={artwork._id}
                        to={`/artworks/${artwork._id}`}
                        className="aging-work-row"
                        aria-label={`${artwork.title}, ${artwork.queueLabel}, ${artwork.ageDays} days since update`}
                      >
                        <span className="aging-work-info">
                          <strong>{artwork.title}</strong>
                          <span>{artwork.queueLabel}</span>
                        </span>
                        <span className="aging-work-age">{artwork.ageDays} days</span>
                        <ArrowRight size={16} aria-hidden="true" />
                      </Link>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
